if (!customElements.get('media-gallery')) {
    class MediaGallery extends HTMLElement {
        constructor() {
            super();

            if (Shopify.designMode) {
                setTimeout(() => this.init(), 200);
            } else {
                this.init();
            }
        }

        disconnectedCallback() {
            window.removeEventListener('on:debounced-resize', this.resizeHandler);
            if (this.viewerClickHandler) this.viewer?.removeEventListener('click', this.viewerClickHandler);
            document.removeEventListener('keydown', this.zoomModalKeydownHandler, true);

            if (this.slideInfoOutsideClickHandler) {
                document.removeEventListener('click', this.slideInfoOutsideClickHandler, true);
            }
            if (this.slideInfoKeydownHandler) {
                document.removeEventListener('keydown', this.slideInfoKeydownHandler);
            }

            if (this.resizeInitHandler) {
                window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
            }

            if (this.zoomInitHandler) {
                window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
            }
        }

        init() {
            this.section = this.closest('.js-product');
            this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled') && this.getMediaGroupData();
            this.stackedScroll = this.dataset.stackedScroll;
            this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
            this.isFeatured = this.dataset.isFeatured === 'true';
            this.viewer = this.querySelector('.media-viewer');
            this.thumbs = this.querySelector('.media-thumbs');
            this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
            this.controls = this.querySelector('.media-ctrl');
            this.prevBtn = this.querySelector('.ctrl-button[name="prev"]');
            this.nextBtn = this.querySelector('.ctrl-button[name="next"]');
            this.counterCurrent = this.querySelector('.media-ctrl__current-item');
            this.counterTotal = this.querySelector('.media-ctrl__total-items');
            this.galleryProgress = this.querySelector('.product-gallery-progress');
            this.liveRegion = this.querySelector('.media-gallery__status');
            this.zoomLinks = this.querySelectorAll('.js-zoom-link');
            this.loadingSpinner = this.querySelector('.loading-spinner');
            this.xrButton = this.querySelector('.media-xr-button');
            this.slideInfoByIndex = this.getSlideInfoByIndex();

            if (this.mediaGroupingEnabled) {
                this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
            }

            if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
                this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
                window.addEventListener('on:debounced-resize', this.resizeInitHandler);
                this.setVisibleItems();
                this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
                setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
            } else {
                this.initGallery();
            }

            if (this.zoomLinks) {
                this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
                this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
                window.addEventListener('on:debounced-resize', this.zoomInitHandler);
                this.initZoom();
                this.zoomLinks.forEach((el) => {
                    el.addEventListener('click', (evt) => {
                        evt.preventDefault();
                    });
                });
            }

            this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
            this.bindGallerySlideInfoDismiss();
            this.updateGallery();
            this.updateThumbnailGrid();
            this.syncGalleryOverlays();
        }

        /**
         * Closes open MEHR LESEN popups when clicking outside or pressing Escape.
         */
        bindGallerySlideInfoDismiss() {
            if (this.slideInfoOutsideClickHandler) return;

            this.slideInfoOutsideClickHandler = (evt) => {
                this.querySelectorAll('.gallery-slide-info__details[open]').forEach((details) => {
                    if (!details.contains(evt.target)) {
                        details.open = false;
                    }
                });
            };

            this.slideInfoKeydownHandler = (evt) => {
                if (evt.key !== 'Escape') return;
                this.querySelectorAll('.gallery-slide-info__details[open]').forEach((details) => {
                    details.open = false;
                });
            };

            // Capture phase so dismiss still works when the info bar stops propagation.
            document.addEventListener('click', this.slideInfoOutsideClickHandler, true);
            document.addEventListener('keydown', this.slideInfoKeydownHandler);
        }

        /**
         * Handle a change in variant on the page.
         * @param {Event} evt - variant change event dispatched by variant-picker
         */
        onVariantChange(evt) {
            if (this.mediaGroupingEnabled) {
                this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
            }

            if (evt.detail.variant && evt.detail.variant.featured_media) {
                const variantMedia = this.viewer.querySelector(`[data-media-id="${evt.detail.variant.featured_media.id}"]`);
                this.customSetActiveMedia(variantMedia, true);
            }
            this.updateGallery();
            this.updateThumbnailGrid();
            this.syncGalleryOverlays();
        }

        /**
         * Gets PDP image info entries keyed by the visible slide number.
         * @returns {Map<number, object>}
         */
        getSlideInfoByIndex() {
            const dataEl = this.querySelector('.js-gallery-slide-info-data');
            const slideInfoByIndex = new Map();

            if (!dataEl) return slideInfoByIndex;

            try {
                const slideInfoEntries = JSON.parse(dataEl.textContent);
                slideInfoEntries.forEach((entry) => {
                    if (entry.index) {
                        slideInfoByIndex.set(Number(entry.index), {
                            title: entry.title || '',
                            description: entry.description || '',
                        });
                    }
                });
            } catch (error) {
                // Keep the gallery working if the metafield contains unexpected data.
            }

            return slideInfoByIndex;
        }

        /**
         * Creates the visible info bar for a gallery slide.
         * @param {object} slideInfo - Parsed slide info entry.
         * @returns {HTMLElement}
         */
        createGallerySlideInfo(slideInfo) {
            const slideInfoEl = document.createElement('div');
            slideInfoEl.className = 'gallery-slide-info';
            slideInfoEl.dataset.generatedSlideInfo = 'true';
            slideInfoEl.addEventListener('click', (evt) => evt.stopPropagation());

            if (slideInfo.description) {
                slideInfoEl.classList.add('gallery-slide-info--interactive');

                const detailsEl = document.createElement('details');
                detailsEl.className = 'gallery-slide-info__details';

                const summaryEl = document.createElement('summary');
                summaryEl.className = 'gallery-slide-info__summary';

                const titleEl = document.createElement('span');
                titleEl.className = 'gallery-slide-info__title';
                titleEl.textContent = slideInfo.title;

                const ctaEl = document.createElement('span');
                ctaEl.className = 'gallery-slide-info__cta';
                ctaEl.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
                        <path d="M6.43898 8.79054C6.57052 8.65949 6.63629 8.49712 6.63629 8.30346C6.63629 8.10979 6.57081 7.94656 6.43985 7.81375C6.3088 7.68104 6.14639 7.61469 5.95262 7.61469C5.75896 7.61469 5.59635 7.68104 5.46481 7.81375C5.33327 7.94656 5.2675 8.10979 5.2675 8.30346C5.2675 8.49712 5.33298 8.65949 5.46394 8.79054C5.59499 8.9216 5.7574 8.98713 5.95117 8.98713C6.14483 8.98713 6.30744 8.9216 6.43898 8.79054ZM5.28835 6.54223H6.61544V2.96202H5.28835V6.54223ZM5.9519 11.9038C5.12638 11.9038 4.35181 11.7477 3.62819 11.4354C2.90447 11.1231 2.27495 10.6994 1.73965 10.1641C1.20444 9.62884 0.780694 8.99933 0.468417 8.2756C0.156139 7.55198 0 6.77741 0 5.9519C0 5.12638 0.156139 4.35181 0.468417 3.62819C0.780694 2.90446 1.20444 2.27495 1.73965 1.73965C2.27495 1.20444 2.90447 0.780694 3.62819 0.468417C4.35181 0.156139 5.12638 0 5.9519 0C6.77741 0 7.55198 0.156139 8.2756 0.468417C8.99933 0.780694 9.62884 1.20444 10.1641 1.73965C10.6994 2.27495 11.1231 2.90446 11.4354 3.62819C11.7477 4.35181 11.9038 5.12638 11.9038 5.9519C11.9038 6.77741 11.7477 7.55198 11.4354 8.2756C11.1231 8.99933 10.6994 9.62884 10.1641 10.1641C9.62884 10.6994 8.99933 11.1231 8.2756 11.4354C7.55198 11.7477 6.77741 11.9038 5.9519 11.9038ZM5.9519 10.5767C7.24534 10.5767 8.33953 10.1293 9.23446 9.23446C10.1293 8.33953 10.5767 7.24534 10.5767 5.9519C10.5767 4.65845 10.1293 3.56426 9.23446 2.66933C8.33953 1.7745 7.24534 1.32708 5.9519 1.32708C4.65845 1.32708 3.56426 1.7745 2.66933 2.66933C1.7745 3.56426 1.32708 4.65845 1.32708 5.9519C1.32708 7.24534 1.7745 8.33953 2.66933 9.23446C3.56426 10.1293 4.65845 10.5767 5.9519 10.5767Z" fill="#18181B"/>
                    </svg>
                    <span>Mehr lesen</span>`;

                summaryEl.appendChild(titleEl);
                summaryEl.appendChild(ctaEl);

                const popupEl = document.createElement('div');
                popupEl.className = 'gallery-slide-info__popup';
                popupEl.textContent = slideInfo.description;

                detailsEl.appendChild(summaryEl);
                detailsEl.appendChild(popupEl);
                slideInfoEl.appendChild(detailsEl);
            } else {
                const titleEl = document.createElement('div');
                titleEl.className = 'gallery-slide-info__title';
                titleEl.textContent = slideInfo.title;
                slideInfoEl.appendChild(titleEl);
            }

            return slideInfoEl;
        }

        /**
         * Returns the currently active main viewer slide.
         * @returns {Element|null}
         */
        getActiveViewerItem() {
            if (this.currentItem && !this.currentItem.classList.contains('hidden')) {
                return this.currentItem;
            }

            return this.querySelector('.media-viewer__item:not(.hidden)');
        }

        /**
         * Returns the primary slide info entry for slide 1 only.
         * Do not fall back to another configured index — that would show e.g. position 3
         * text on the first image when slide 1 has no entry.
         * @returns {object|null}
         */
        getPrimarySlideInfo() {
            if (!this.slideInfoByIndex || this.slideInfoByIndex.size === 0) return null;

            return this.slideInfoByIndex.get(1) || null;
        }

        /**
         * Keeps the current-variant marker on the active main slide.
         * @param {Element} mediaItem - Media element to mark as current.
         */
        updateCurrentVariantClass(mediaItem) {
            if (!mediaItem) return;

            this.querySelectorAll('.media-viewer__item.is-current-variant').forEach((item) => {
                item.classList.remove('is-current-variant');
            });
            mediaItem.classList.add('is-current-variant');
        }

        /**
         * Moves the product badge onto the active main slide so it survives variant changes.
         * Desktop only: the mobile swiper shows every slide at once, so the badge stays on
         * the first item where it is rendered instead of following the active slide.
         */
        syncGalleryBadge() {
            if (!theme.mediaMatches.md) return;

            const badge = this.querySelector('.media-gallery__badge');
            const target = this.getActiveViewerItem();

            if (!badge || !target) return;

            const mediaContainer = target.querySelector('.media, deferred-media, product-model') || target;
            if (badge.parentElement === mediaContainer) return;

            mediaContainer.appendChild(badge);
        }

        /**
         * Keeps slide info and the product badge on the active main slide.
         */
        syncGalleryOverlays() {
            this.syncGallerySlideInfo();
            this.syncGalleryBadge();
        }

        /**
         * Places info bars on gallery items that match their stable slide index.
         */
        syncGallerySlideInfo() {
            this.querySelectorAll('[data-generated-slide-info="true"]').forEach((slideInfo) => slideInfo.remove());

            if (!this.slideInfoByIndex || this.slideInfoByIndex.size === 0) return;

            const viewerItems = Array.from(this.querySelectorAll('.media-viewer__item')).filter((item) => !item.classList.contains('hidden'));
            viewerItems.forEach((item) => {
                const slideIndex = Number(item.dataset.slideIndex);
                const slideInfo = slideIndex ? this.slideInfoByIndex.get(slideIndex) : null;
                if (slideInfo) {
                    item.appendChild(this.createGallerySlideInfo(slideInfo));
                }
            });

            // Desktop only: the single main viewer falls back to the primary slide info when the
            // active slide has none. On the mobile swiper every slide is visible, so each item must
            // show only its own configured info instead of inheriting the primary entry.
            if (theme.mediaMatches.md) {
                const activeItem = this.getActiveViewerItem();
                if (activeItem && !activeItem.querySelector('.gallery-slide-info')) {
                    const primarySlideInfo = this.getPrimarySlideInfo();
                    if (primarySlideInfo) {
                        activeItem.appendChild(this.createGallerySlideInfo(primarySlideInfo));
                    }
                }
            }

            // Desktop media grid can show slide info on large cells; mobile 64px thumbs cannot.
            if (!this.thumbs || !theme.mediaMatches.md) return;

            const visibleThumbs = Array.from(this.thumbs.querySelectorAll('.media-thumbs__item')).filter(
                (thumb) => !thumb.classList.contains('hidden') && !thumb.classList.contains('media-thumbs__item--excluded-from-grid')
            );
            visibleThumbs.forEach((thumb) => {
                const slideIndex = Number(thumb.dataset.slideIndex);
                const slideInfo = slideIndex ? this.slideInfoByIndex.get(slideIndex) : null;
                if (slideInfo) {
                    thumb.appendChild(this.createGallerySlideInfo(slideInfo));
                }
            });
        }

        /**
         * Gets the media group from currently selected variant options.
         * @returns {?object}
         */
        getMediaGroupFromOptionSelectors() {
            const optionSelectors = this.section.querySelectorAll('.option-selector');
            if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
                const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
                if (selector.dataset.selectorType === 'dropdown') {
                    return selector.querySelector('.custom-select__btn').textContent.trim();
                }
                return selector.querySelector('input:checked').value;
            }
            return null;
        }

        /**
         * Gets the variant media associations for a product.
         * @returns {?object}
         */
        getMediaGroupData() {
            if (typeof this.variantMediaData === 'undefined') {
                const dataEl = this.querySelector('.js-data-variant-media');
                this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
            }

            return this.variantMediaData;
        }

        /**
         * Gets an object mapping media to groups, and the reverse
         * @returns {?object}
         */
        getMediaGroupMap() {
            if (!this.mediaGroupMap) {
                this.mediaGroupMap = {
                    groups: {},
                };

                // set up grouping
                const variantMediaData = this.getMediaGroupData();
                let currentMediaOptionName = false;
                this.viewerItems = this.querySelectorAll('.media-viewer__item');
                this.viewerItems.forEach((item) => {
                    currentMediaOptionName = false;
                    for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
                        if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
                            if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                                currentMediaOptionName = variantMediaData.variantMedia[i].option;
                            }
                        }
                    }
                    if (!currentMediaOptionName) {
                        currentMediaOptionName = 'ambient';
                    } else {}

                    if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
                        this.mediaGroupMap.groups[currentMediaOptionName] = {
                            name: currentMediaOptionName,
                            items: [],
                        };
                    }
                    const groupItem = { main: item };
                    if (this.thumbs) {
                        groupItem.thumb = this.thumbs.querySelector(`[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`);
                    }
                    this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
                });

                // add helper
                this.mediaGroupMap.groupFromItem = (item) => {
                    const groups = Object.keys(this.mediaGroupMap.groups);
                    for (let i = 0; i < groups; i += 1) {
                        const group = groups[i];
                        for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
                            if (this.mediaGroupMap.groups[group].items[j] === item) {
                                return this.mediaGroupMap.groups[group];
                            }
                        }
                    }
                    return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
                };
            }

            return this.mediaGroupMap;
        }

        /**
         * Show only images associated to the current variant
         * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
         */
        setActiveMediaGroup(groupName) {
            const mediaGroupMap = this.getMediaGroupMap();
            const selectedGroup = groupName ? mediaGroupMap.groups[groupName] : mediaGroupMap.groupFromItem(this.currentItem);

            if (selectedGroup) {
                if (this.currentGroup !== selectedGroup) {
                    this.currentGroup = selectedGroup;
                    this.viewerItems.forEach((item) => {
                        item.classList.add('hidden');
                        item.classList.remove('media-viewer__item--single');
                    });
                    this.thumbsItems.forEach((item) => {
                        item.classList.add('hidden');
                    });

                    let currentItemIsVisible = false;
                    selectedGroup.items.forEach((item) => {
                        item.main.classList.remove('hidden');
                        if (item.thumb) {
                            item.thumb.classList.remove('hidden');
                        }
                        if (item.main === this.currentItem) {
                            currentItemIsVisible = true;
                        }
                    });

                    const ambient = mediaGroupMap.groups['ambient'];
                    if (ambient && ambient.items) { 
                        ambient.items.forEach((item) => {
                            item.main.classList.remove('hidden');
                            if (item.thumb) {
                                item.thumb.classList.remove('hidden');
                            }
                            if (item.main === this.currentItem) {
                                currentItemIsVisible = true;
                            }
                        });
                    }

                    this.setVisibleItems();

                    // If current item is not in this group, set it as the active item
                    if (!currentItemIsVisible) {
                        this.customSetActiveMedia(selectedGroup.items[0].main, true);
                    }

                    // Handle single images on stacked view
                    if (selectedGroup.items.length === 1) {
                        selectedGroup.items[0].main.classList.add('media-viewer__item--single');
                    }
                }
            } else {
                this.viewerItems.forEach((item) => {
                    item.classList.remove('hidden');
                });
                this.thumbsItems.forEach((item) => {
                    item.classList.remove('hidden');
                });
            }
            this.updateThumbnailGrid();
            this.syncGalleryOverlays();
        }

        updateGallery() {
            const visibleItems = Array.from(
                document.querySelectorAll('.product-media--stacked .media-viewer__item:not(.media-viewer__item--single)')
            ).filter((item) => !item.classList.contains('hidden') && getComputedStyle(item).display !== 'none');

            const videoItem = visibleItems.find((item) => {
                const type = item.dataset.mediaType;
                return type === 'video' || type === 'external_video';
            });

            visibleItems.forEach((item) => {
                item.style.flex = '';
            });

            if (videoItem) {
                const firstItem = visibleItems[0];
                if (firstItem && videoItem !== firstItem && visibleItems[1] !== videoItem) {
                    firstItem.after(videoItem);
                }
            } else {
                visibleItems.forEach((item, index) => {
                    if (index === 0 || index % 3 === 0) {
                        item.style.flex = '100%';
                    }
                });
            }

            this.updateHasVideoHeroPair();
        }

        /**
         * With video (slider): keep only main image + video in the top row,
         * autoplay the video, and leave remaining media in the limited thumb grid.
         */
        updateHasVideoHeroPair() {
            const items = Array.from(this.querySelectorAll('.media-viewer__item'));
            items.forEach((item) => item.classList.remove('media-viewer__item--hero-pair'));

            if (!this.classList.contains('media-gallery--has-video') || this.dataset.layout !== 'slider') {
                return;
            }

            const visibleItems = items.filter((item) => !item.classList.contains('hidden'));
            const videoItem = visibleItems.find((item) => {
                const type = item.dataset.mediaType;
                return type === 'video' || type === 'external_video';
            });
            if (!videoItem) return;

            let firstImage = visibleItems.find((item) => {
                const type = item.dataset.mediaType;
                return type !== 'video' && type !== 'external_video';
            });

            const currentItem = this.querySelector('.media-viewer__item.is-current-variant:not(.hidden)');
            if (
                currentItem
                && currentItem !== videoItem
                && currentItem.dataset.mediaType !== 'video'
                && currentItem.dataset.mediaType !== 'external_video'
            ) {
                firstImage = currentItem;
            }

            if (!firstImage) return;

            if (firstImage.nextElementSibling !== videoItem) {
                firstImage.after(videoItem);
            }

            firstImage.classList.add('media-viewer__item--hero-pair');
            videoItem.classList.add('media-viewer__item--hero-pair');
            MediaGallery.playActiveMedia(videoItem);
        }

        /**
         * Hides thumbnails that match media already shown in the main viewer.
         * Desktop grid only — mobile keeps a full scrollable thumb strip.
         */
        updateMainThumbExclusion() {
            if (!this.thumbs) return;

            if (!theme.mediaMatches.md) {
                this.thumbs.querySelectorAll('.media-thumbs__item').forEach((thumb) => {
                    thumb.classList.remove('media-thumbs__item--excluded-from-grid');
                });
                return;
            }

            const excludeIds = new Set();
            const currentMediaId = this.currentItem?.dataset.mediaId
                || this.querySelector('.media-viewer__item.is-current-variant')?.dataset.mediaId;

            if (currentMediaId) excludeIds.add(String(currentMediaId));

            if (this.classList.contains('media-gallery--has-video')) {
                this.querySelectorAll('.media-viewer__item--hero-pair').forEach((item) => {
                    if (item.dataset.mediaId) excludeIds.add(String(item.dataset.mediaId));
                });
            }

            this.thumbs.querySelectorAll('.media-thumbs__item').forEach((thumb) => {
                const isExcluded = excludeIds.has(String(thumb.dataset.mediaId));
                thumb.classList.toggle('media-thumbs__item--excluded-from-grid', isExcluded);

                if (isExcluded) {
                    const btn = thumb.querySelector('.media-thumbs__btn');
                    btn?.classList.remove('is-active');
                    btn?.removeAttribute('aria-current');
                }
            });
        }

        /**
         * Pins the first product video to the first thumbnail-grid slot (top-left).
         * Main media is excluded from the grid, so that slot is index 0 among visible thumbs.
         * @param {Element[]} visibleThumbs - Thumbs that are not hidden/excluded.
         * @returns {Element[]}
         */
        pinVideoToFirstThumbnailSlot(visibleThumbs) {
            if (!this.thumbs || visibleThumbs.length < 1) return visibleThumbs;
            if (this.classList.contains('media-gallery--has-video')) return visibleThumbs;

            const videoIndex = visibleThumbs.findIndex((thumb) => {
                const mediaType = this.viewer?.querySelector(
                    `.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`
                )?.dataset.mediaType;
                return mediaType === 'video' || mediaType === 'external_video' || !!thumb.querySelector('.media-thumbs__video');
            });

            if (videoIndex < 0 || videoIndex === 0) return visibleThumbs;

            this.thumbs.insertBefore(visibleThumbs[videoIndex], visibleThumbs[0]);

            return Array.from(this.thumbs.querySelectorAll('.media-thumbs__item')).filter(
                (thumb) => !thumb.classList.contains('hidden') && !thumb.classList.contains('media-thumbs__item--excluded-from-grid')
            );
        }

        /**
         * Marks the first N currently visible thumbnails for the media grid.
         * N comes from data-grid-limit (theme setting). Media grouping can hide
         * variant thumbnails, so CSS nth-child cannot be used here.
         * Mobile uses a full horizontal strip (no +N / grid limiting).
         */
        updateThumbnailGrid() {
            this.updateMainThumbExclusion();
            if (!this.thumbs || !this.thumbs.classList.contains('media-thumbs--grid')) return;

            const thumbs = Array.from(this.thumbs.querySelectorAll('.media-thumbs__item'));
            thumbs.forEach((thumb) => {
                thumb.classList.remove('media-thumbs__item--grid-visible', 'media-thumbs__item--has-grid-more');
                const more = thumb.querySelector('.media-thumbs__grid-more');
                if (more) more.remove();
            });

            if (!theme.mediaMatches.md) {
                this.updateThumbnailVideoAutoplay([]);
                this.syncGalleryOverlays();
                return;
            }

            const gridLimit = Math.max(2, parseInt(this.dataset.gridLimit || '4', 10) || 4);
            let visibleThumbs = thumbs.filter(
                (thumb) => !thumb.classList.contains('hidden') && !thumb.classList.contains('media-thumbs__item--excluded-from-grid')
            );
            visibleThumbs = this.pinVideoToFirstThumbnailSlot(visibleThumbs);
            const lastVisibleIndex = gridLimit - 1;
            visibleThumbs.slice(0, gridLimit).forEach((thumb, index) => {
                thumb.classList.add('media-thumbs__item--grid-visible');

                if (index === lastVisibleIndex && visibleThumbs.length > gridLimit) {
                    thumb.classList.add('media-thumbs__item--has-grid-more');
                    const more = document.createElement('span');
                    more.className = 'media-thumbs__grid-more';
                    more.setAttribute('aria-hidden', 'true');
                    more.textContent = `+${visibleThumbs.length - lastVisibleIndex}`;
                    thumb.querySelector('.media-thumbs__btn')?.appendChild(more);
                }
            });
            this.updateThumbnailVideoAutoplay(visibleThumbs);
            this.syncGalleryOverlays();
        }

        /**
         * Autoplays thumbnail videos in visible grid slots (except the +N tile).
         * @param {Element[]} [visibleThumbs] - Currently visible thumbnail items.
         */
        updateThumbnailVideoAutoplay(visibleThumbs = null) {
            if (!this.thumbs) return;

            const gridLimit = Math.max(2, parseInt(this.dataset.gridLimit || '4', 10) || 4);
            const thumbs = Array.from(this.thumbs.querySelectorAll('.media-thumbs__item'));
            const playableThumbs = visibleThumbs || thumbs.filter(
                (thumb) => !thumb.classList.contains('hidden') && !thumb.classList.contains('media-thumbs__item--excluded-from-grid')
            );
            const canAutoplay = this.thumbs.classList.contains('media-thumbs--grid') && this.thumbs.offsetParent !== null;

            thumbs.forEach((thumb) => {
                const video = thumb.querySelector('.media-thumbs__video');
                if (!video) return;

                const visibleIndex = playableThumbs.indexOf(thumb);
                const shouldAutoplay = canAutoplay
                    && visibleIndex > -1
                    && visibleIndex < gridLimit
                    && !thumb.classList.contains('media-thumbs__item--has-grid-more');
                thumb.classList.toggle('media-thumbs__item--autoplay-video', shouldAutoplay);
                video.muted = true;
                video.playsInline = true;

                if (shouldAutoplay) {
                    video.play().catch(() => {});
                } else {
                    video.pause();
                }
            });
        }

        /**
         * Initialises the media gallery slider and associated controls.
         */
        initGallery() {
            this.setVisibleItems();
            if (this.visibleItems.length <= 1) return;

            this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
            this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
            this.currentItem = this.visibleItems[this.currentIndex];
            this.addListeners();

            if (this.thumbs && this.currentItem) {
                this.currentThumb = this.thumbs.querySelector(`[data-media-id="${this.currentItem.dataset.mediaId}"]`);
            }

            if (!this.isFeatured && document.hasFocus()) {
                // Eager load the slider images for smooth UX
                this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
                    setTimeout(() => {
                        img.loading = 'eager';
                    }, 500 * (index + 1));
                });
            }

            const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
            this.customSetActiveMedia(currentItem, true);
            this.updateThumbnailGrid();
            this.syncGalleryOverlays();
        }

        addListeners() {
            this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
            this.viewerClickHandler = this.viewerClickHandler || this.handleViewerClick.bind(this);
            this.viewer.addEventListener('click', this.viewerClickHandler);
            if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
            if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
            this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
            window.addEventListener('on:debounced-resize', this.resizeHandler);
        }

        /**
         * Initialized the zoom on hover for desktop
         */
        initZoom() {
            this.zoomLinks.forEach((el) => {
                const zoomWidth = Number(el.querySelector('.zoom-image')?.dataset.originalWidth || 0);
                const imageWidth = el.querySelector('.product-image')?.getBoundingClientRect().width || 0;
                // Always keep links clickable so the full image opens the lightbox
                el.classList.remove('pointer-events-none');
                if (theme.mediaMatches.md && zoomWidth - 75 > imageWidth) {
                    el.addEventListener('mousemove', this.zoomEventListener);
                } else {
                    el.removeEventListener('mousemove', this.zoomEventListener);
                }
            });
        }

        /**
         * Handles mouse move over a zoomable image
         * @param {?object} evt - Event object.
         */
        handleZoomMouseMove(evt) {
            const hoverElem = evt.currentTarget;
            const zoomImage = hoverElem.querySelector('.js-zoom-image');

            // Download the zoom image if necessary
            if (zoomImage.dataset.src) {
                this.loadingSpinner.classList.remove('loading-spinner--out');

                const img = new Image();
                img.src = zoomImage.dataset.src;
                img.onload = () => {
                    zoomImage.src = img.src;
                    hoverElem.classList.remove('media--zoom-not-loaded');
                    this.loadingSpinner.classList.add('loading-spinner--out');
                };
                zoomImage.removeAttribute('data-src');
            }

            try {
                const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
                const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
                const x = (offsetX / zoomImage.offsetWidth) * 100;
                const y = (offsetY / zoomImage.offsetHeight) * 100;
                zoomImage.style.objectPosition = `${x}% ${y}%`;
            } catch (err) {
                // Fail silently
            }
        }

        /**
         * Handles 'scroll' events on the main media container.
         */
        handleScroll() {
            if (!this.viewer || !this.visibleItems || this.visibleItems.length < 2) return;

            const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
            if (viewerItemOffset <= 0) return;

            // Keep offset in sync with layout (gap/resize) so swipe index stays accurate.
            this.viewerItemOffset = viewerItemOffset;
            const newIndex = Math.min(
                Math.max(Math.round(this.viewer.scrollLeft / viewerItemOffset), 0),
                this.visibleItems.length - 1
            );

            if (newIndex !== this.currentIndex) {
                this.customSetActiveMedia(this.visibleItems[newIndex], false);
            }
        }

        /**
         * Opens the zoom modal when clicking the main media (full image area or zoom icon).
         * @param {object} evt - Event object.
         */
        handleViewerClick(evt) {
            // Keep info overlays, badges, and video posters interactive without opening zoom
            if (evt.target.closest('.gallery-slide-info, .media-gallery__badge-trigger, .js-load-media, .media-poster')) {
                return;
            }

            const mediaItem = evt.target.closest('.media-viewer__item');
            if (!mediaItem) return;
            if (!mediaItem.querySelector('.js-product-zoom-trigger, .js-zoom-link')) return;

            evt.preventDefault();
            this.openZoomForMedia(mediaItem);
        }

        /**
         * Handles 'click' events on the controls container.
         * @param {object} evt - Event object.
         */
        handleNavClick(evt) {
            const control = evt.target.closest('.ctrl-button');
            if (!control) return;

            evt.preventDefault();
            this.setVisibleItems();

            const viewerItemOffset = this.visibleItems[1]
                ? this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft
                : this.viewer.clientWidth;
            const scrollIndex = viewerItemOffset ? Math.round(this.viewer.scrollLeft / viewerItemOffset) : this.currentIndex;
            const currentIndex = Math.min(Math.max(scrollIndex, 0), this.visibleItems.length - 1);
            const direction = control.name === 'next' ? 1 : -1;
            const itemToShow = this.visibleItems[Math.min(Math.max(currentIndex + direction, 0), this.visibleItems.length - 1)];

            if (!itemToShow) return;

            this.customSetActiveMedia(itemToShow);
        }

        /**
         * Handles thumbnail clicks.
         * Mobile strip: jump to that slide. Desktop grid: open lightbox.
         * @param {object} evt - Event object.
         */
        handleThumbClick(evt) {
            const thumb = evt.target.closest('.media-thumbs__item[data-media-id]');
            if (!thumb) return;
            if (evt.target.closest('.gallery-slide-info')) return;

            evt.preventDefault();

            const mediaItem = this.viewer.querySelector(`.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`);
            if (!mediaItem) return;

            if (!theme.mediaMatches.md) {
                this.customSetActiveMedia(mediaItem, true);
                return;
            }

            this.openZoomForMedia(mediaItem);
        }

        /**
         * Opens the zoom target for the selected image.
         * @param {Element} mediaItem - Media item whose zoom image should be opened.
         */
        openZoomForMedia(mediaItem) {
            if (!mediaItem) return;

            this.zoomItems = Array.from(this.querySelectorAll('.media-viewer__item')).filter(
                (item) => !item.classList.contains('hidden')
            );
            this.zoomIndex = this.zoomItems.indexOf(mediaItem);
            if (this.zoomIndex < 0) this.zoomIndex = 0;

            this.createZoomModal();
            this.renderZoomModal();

            document.body.classList.add('fixed');
            this.zoomModal.removeAttribute('hidden');
            this.zoomModal.setAttribute('aria-hidden', 'false');
            this.zoomModal.querySelector('.js-product-zoom-close')?.focus();

            this.zoomModalKeydownHandler = this.zoomModalKeydownHandler || this.handleZoomModalKeydown.bind(this);
            document.addEventListener('keydown', this.zoomModalKeydownHandler, true);
        }

        /**
         * Builds the product zoom modal once.
         */
        createZoomModal() {
            if (this.zoomModal) return;

            this.zoomModal = document.createElement('div');
            this.zoomModal.className = 'product-zoom-modal';
            this.zoomModal.setAttribute('hidden', '');
            this.zoomModal.setAttribute('aria-hidden', 'true');
            this.zoomModal.setAttribute('role', 'dialog');
            this.zoomModal.setAttribute('aria-modal', 'true');
            this.zoomModal.innerHTML = `
                <button type="button" class="product-zoom-modal__close js-product-zoom-close" aria-label="Close">
                    &times;
                </button>
                <button type="button" class="product-zoom-modal__nav product-zoom-modal__nav--prev js-product-zoom-prev" aria-label="Previous">
                    <svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none" aria-hidden="true" focusable="false" role="presentation" class="icon product-zoom-modal__arrow product-zoom-modal__arrow--prev">
                        <path d="M3.83333 4.71146L0 0.878125L0.878125 0L5.58958 4.71146L0.878125 9.42292L0 8.54479L3.83333 4.71146Z" fill="currentColor"/>
                    </svg>
                </button>
                <div class="product-zoom-modal__content">
                    <div class="product-zoom-modal__media js-product-zoom-media"></div>
                    <div class="product-zoom-modal__thumbs js-product-zoom-thumbs" role="tablist" hidden></div>
                </div>
                <button type="button" class="product-zoom-modal__nav product-zoom-modal__nav--next js-product-zoom-next" aria-label="Next">
                    <svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none" aria-hidden="true" focusable="false" role="presentation" class="icon product-zoom-modal__arrow">
                        <path d="M3.83333 4.71146L0 0.878125L0.878125 0L5.58958 4.71146L0.878125 9.42292L0 8.54479L3.83333 4.71146Z" fill="currentColor"/>
                    </svg>
                </button>
                <div class="product-zoom-modal__counter js-product-zoom-counter"></div>
            `;

            this.zoomModal.addEventListener('click', (evt) => {
                if (evt.target === this.zoomModal || evt.target.closest('.js-product-zoom-close')) {
                    this.closeZoomModal();
                } else if (evt.target.closest('.js-product-zoom-prev')) {
                    this.showZoomMedia(this.zoomIndex - 1);
                } else if (evt.target.closest('.js-product-zoom-next')) {
                    this.showZoomMedia(this.zoomIndex + 1);
                } else if (evt.target.closest('.js-product-zoom-thumb')) {
                    const thumbBtn = evt.target.closest('.js-product-zoom-thumb');
                    const index = Number(thumbBtn.dataset.index);
                    if (!Number.isNaN(index)) {
                        this.showZoomMedia(index);
                    }
                }
            });

            document.body.appendChild(this.zoomModal);
        }

        /**
         * Handles keyboard controls while product zoom is open.
         * @param {KeyboardEvent} evt - Keydown event.
         */
        handleZoomModalKeydown(evt) {
            if (!this.zoomModal || this.zoomModal.hasAttribute('hidden')) return;

            if (evt.key === 'Escape') {
                evt.preventDefault();
                evt.stopPropagation();
                this.closeZoomModal();
            } else if (evt.key === 'ArrowLeft') {
                evt.preventDefault();
                this.showZoomMedia(this.zoomIndex - 1);
            } else if (evt.key === 'ArrowRight') {
                evt.preventDefault();
                this.showZoomMedia(this.zoomIndex + 1);
            }
        }

        /**
         * Closes the product zoom modal.
         */
        closeZoomModal() {
            if (!this.zoomModal) return;

            window.pauseAllMedia(this.zoomModal);
            this.zoomModal.querySelector('.js-product-zoom-media').innerHTML = '';
            const thumbsContainer = this.zoomModal.querySelector('.js-product-zoom-thumbs');
            if (thumbsContainer) {
                thumbsContainer.innerHTML = '';
                thumbsContainer.hidden = true;
            }
            this.zoomModal.setAttribute('hidden', '');
            this.zoomModal.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('fixed');
            document.removeEventListener('keydown', this.zoomModalKeydownHandler, true);
        }

        /**
         * Shows a media item in the product zoom modal.
         * @param {number} index - Media index in the visible gallery.
         */
        showZoomMedia(index) {
            if (!this.zoomItems?.length) return;

            window.pauseAllMedia(this.zoomModal);

            const max = this.zoomItems.length - 1;
            this.zoomIndex = index < 0 ? max : index > max ? 0 : index;
            this.renderZoomModal();
        }

        /**
         * Renders the currently selected media in the product zoom modal.
         */
        renderZoomModal() {
            if (!this.zoomModal || !this.zoomItems?.length) return;

            const mediaItem = this.zoomItems[this.zoomIndex];
            const mediaContainer = this.zoomModal.querySelector('.js-product-zoom-media');
            const counter = this.zoomModal.querySelector('.js-product-zoom-counter');
            const mediaType = mediaItem.dataset.mediaType;
            const sourceImage = mediaItem.querySelector('.product-image, .media-poster img');
            const zoomLink = mediaItem.querySelector('.js-zoom-link');
            const src = zoomLink?.href || sourceImage?.currentSrc || sourceImage?.src;
            const alt = sourceImage?.alt || '';

            mediaContainer.innerHTML = '';
            if (mediaType === 'video' || mediaType === 'external_video') {
                const deferredTemplate = mediaItem.querySelector('deferred-media template');
                const loadedMedia = mediaItem.querySelector('deferred-media video, deferred-media iframe');
                const modalMedia = deferredTemplate?.content.firstElementChild.cloneNode(true) || loadedMedia?.cloneNode(true);

                if (modalMedia) {
                    modalMedia.classList.add('product-zoom-modal__video');
                    mediaContainer.appendChild(modalMedia);

                    if (modalMedia.tagName === 'VIDEO') {
                        modalMedia.controls = true;
                        modalMedia.autoplay = true;
                        modalMedia.playsInline = true;
                        modalMedia.play().catch(() => {
                            modalMedia.muted = true;
                            modalMedia.play().catch(() => {});
                        });
                    } else {
                        const allow = modalMedia.getAttribute('allow') || '';
                        if (!allow.includes('autoplay')) {
                            modalMedia.setAttribute('allow', `${allow}; autoplay`.trim());
                        }
                    }
                }

                counter.textContent = `${this.zoomIndex + 1} / ${this.zoomItems.length}`;
                this.renderZoomThumbnails();
                return;
            }

            if (src) {
                const image = document.createElement('img');
                image.className = 'product-zoom-modal__image';
                image.src = src;
                image.alt = alt;
                mediaContainer.appendChild(image);
            }
            counter.textContent = `${this.zoomIndex + 1} / ${this.zoomItems.length}`;
            this.renderZoomThumbnails();
        }

        /**
         * Renders the thumbnail strip in the product zoom modal.
         */
        renderZoomThumbnails() {
            const thumbsContainer = this.zoomModal?.querySelector('.js-product-zoom-thumbs');
            if (!thumbsContainer || !this.zoomItems?.length) return;

            thumbsContainer.innerHTML = '';

            if (this.zoomItems.length <= 1) {
                thumbsContainer.hidden = true;
                return;
            }

            thumbsContainer.hidden = false;

            this.zoomItems.forEach((mediaItem, index) => {
                const mediaType = mediaItem.dataset.mediaType;
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'product-zoom-modal__thumb js-product-zoom-thumb';
                btn.dataset.index = index;
                btn.setAttribute('role', 'tab');
                btn.setAttribute('aria-label', `${index + 1} / ${this.zoomItems.length}`);

                if (index === this.zoomIndex) {
                    btn.classList.add('is-active');
                    btn.setAttribute('aria-selected', 'true');
                } else {
                    btn.setAttribute('aria-selected', 'false');
                }

                const galleryThumbBtn = this.thumbs?.querySelector(
                    `.media-thumbs__item[data-media-id="${mediaItem.dataset.mediaId}"] .media-thumbs__btn`
                );

                if (galleryThumbBtn) {
                    const thumbImage = galleryThumbBtn.querySelector('img');
                    if (thumbImage) {
                        const image = thumbImage.cloneNode(true);
                        image.classList.add('product-zoom-modal__thumb-image');
                        image.removeAttribute('loading');
                        btn.appendChild(image);
                    }

                    const badge = galleryThumbBtn.querySelector('.media-thumbs__badge');
                    if (badge) {
                        btn.appendChild(badge.cloneNode(true));
                    }
                } else {
                    const sourceImage = mediaItem.querySelector('.product-image, .media-poster img, img');
                    if (sourceImage) {
                        const image = document.createElement('img');
                        image.className = 'product-zoom-modal__thumb-image';
                        image.src = sourceImage.currentSrc || sourceImage.src;
                        image.alt = sourceImage.alt || '';
                        btn.appendChild(image);
                    }
                }

                if (mediaType === 'video' || mediaType === 'external_video') {
                    btn.classList.add('product-zoom-modal__thumb--video');
                }

                if (mediaItem.classList.contains('media-viewer__item--energy-label')) {
                    btn.classList.add('product-zoom-modal__thumb--energy-label');
                }

                thumbsContainer.appendChild(btn);
            });

            const activeThumb = thumbsContainer.querySelector('.js-product-zoom-thumb.is-active');
            if (activeThumb) {
                const scrollLeft = activeThumb.offsetLeft - (thumbsContainer.clientWidth / 2) + (activeThumb.clientWidth / 2);
                thumbsContainer.scrollTo({ left: Math.max(scrollLeft, 0), behavior: 'smooth' });
            }
        }

        /**
         * Handles debounced 'resize' events on the window.
         */
        handleResize() {
            // Reset distance from leading edge of one slide to the next.
            this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

            this.updateThumbnailGrid();
            if (this.thumbs && this.currentItem) {
                this.setActiveThumb();
            } else if (this.thumbs && this.currentThumb) {
                this.checkThumbVisibilty(this.currentThumb);
            }
        }

        /**
         * Stub for variant-picker calls. Listening to change event instead.
         */
        // eslint-disable-next-line class-methods-use-this
        setActiveMedia() {}

        /**
         * Sets the active media item.
         * @param {Element} mediaItem - Media element to set as active.
         * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
         */
        customSetActiveMedia(mediaItem, scrollToItem = true) {
            if (!mediaItem) return;

            if (mediaItem === this.currentItem) {
                this.updateGalleryProgress();
                this.syncGalleryOverlays();
                if (!theme.mediaMatches.md) {
                    MediaGallery.playActiveMedia(mediaItem);
                }
                return;
            }
            window.pauseAllMedia(this);
            this.currentItem = mediaItem;
            this.currentIndex = this.visibleItems.indexOf(this.currentItem);
            this.updateCurrentVariantClass(mediaItem);

            if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
                // Update the active class and scroll to the active media
                if (this.stackedUnderline) {
                    if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
                    mediaItem.classList.add('is-active');
                    this.previousMediaItem = mediaItem;
                }

                if (this.stackedScroll !== 'never') {
                    const y = mediaItem.getBoundingClientRect().top + document.documentElement.scrollTop - 150;

                    // If the element is far enough away to scroll to it
                    if (Math.abs(y - document.documentElement.scrollTop) > 300) {
                        window.scrollTo({
                            top: y < 100 ? 0 : y,
                            behavior: 'smooth',
                        });
                    }
                }
                this.syncGalleryOverlays();
                return;
            }

            if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });

            if (this.controls) {
                if (this.prevBtn) {
                    this.prevBtn.disabled = this.currentIndex === 0;
                }

                if (this.nextBtn) {
                    this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
                }

                if (this.counterCurrent) {
                    this.counterCurrent.textContent = this.currentIndex + 1;
                }
            }
            this.updateGalleryProgress();

            this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

            if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
                this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
            }

            this.updateThumbnailGrid();

            // After grid/exclusion updates so the mobile strip keeps the current thumb active.
            if (this.thumbs) this.setActiveThumb();

            if (!theme.mediaMatches.md) {
                MediaGallery.playActiveMedia(mediaItem);
            }
        }

        /**
         * Sets the active thumbnail.
         */
        setActiveThumb() {
            if (!this.thumbs || !this.currentItem) return;

            this.currentThumb = this.thumbs.querySelector(`[data-media-id="${this.currentItem.dataset.mediaId}"]`);
            if (!this.currentThumb) return;

            const btn = this.currentThumb.querySelector('button');
            if (!btn) return;

            this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
                el.classList.remove('is-active');
                el.removeAttribute('aria-current');
            });

            btn.classList.add('is-active');
            btn.setAttribute('aria-current', 'true');
            this.checkThumbVisibilty(this.currentThumb);
        }

        /**
         * Creates an array of the visible media items.
         */
        setVisibleItems() {
            this.viewerItems = this.querySelectorAll('.media-viewer__item');
            this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
            if (this.counterTotal) {
                this.counterTotal.textContent = this.visibleItems.length;
            }
            this.updateGalleryProgress();
        }

        /**
         * Updates the mobile gallery progress bar.
         */
        updateGalleryProgress() {
            if (!this.galleryProgress || !this.visibleItems) return;

            const count = this.visibleItems.length;
            if (count < 1) {
                this.galleryProgress.hidden = true;
                return;
            }

            const index = Math.max(this.currentIndex || 0, 0);
            this.galleryProgress.style.setProperty('--product-gallery-progress-count', count);
            this.galleryProgress.style.setProperty('--product-gallery-progress-current', Math.min(index + 1, count));
            this.galleryProgress.hidden = count <= 1;
        }

        /**
         * Ensures a thumbnail is in the visible area of the slider.
         * @param {Element} thumb - Thumb item element.
         */
        checkThumbVisibilty(thumb) {
            const scrollPos = this.thumbs.scrollLeft;
            const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
            const thumbOffset = thumb.offsetLeft;

            if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
                this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
            }
        }

        /**
         * Updates the media gallery status.
         * @param {Element} mediaItem - Active media element.
         * @param {number} index - Active media index.
         */
        announceLiveRegion(mediaItem, index) {
            const image = mediaItem.querySelector('.media-viewer img');
            if (!image) return;

            this.liveRegion.setAttribute('aria-hidden', 'false');
            this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

            setTimeout(() => {
                this.liveRegion.setAttribute('aria-hidden', 'true');
            }, 2000);
        }

        /**
         * Loads and plays deferred video media for the active item.
         * @param {Element} mediaItem - Active media element.
         */
        static playActiveMedia(mediaItem) {
            if (!mediaItem) return;

            const mediaType = mediaItem.dataset.mediaType;
            if (mediaType !== 'video' && mediaType !== 'external_video') return;

            const deferredMedia = mediaItem.querySelector('deferred-media');
            if (!deferredMedia) return;

            if (deferredMedia.getAttribute('loaded') === null) {
                deferredMedia.loadContent(false, false, 'autoplay');
            }

            const video = deferredMedia.querySelector('video');
            if (!video) return;

            video.muted = true;
            video.playsInline = true;
            video.autoplay = true;
            video.loop = true;
            video.play().catch(() => {});
        }
    }

    customElements.define('media-gallery', MediaGallery);
}
